<template>
  <Section>
    <VideoUploaderModal
      v-if="uploadModal"
      :onClose="onCloseModal"
      :onVideoChange="onVideoChange"
      :onDeleteVideo="onDeleteVideo"
      :isEdit="true"/>
    <template v-slot:section-header>
      <h5 class="title">ข้อมูลวิดีโอ</h5>
    </template>
    <template v-slot:section-body v-if="currentVideo">
      <form>
        <div class="row">
          <div class="col-md-8">
            <FormGroup :errorMessage="getErrorMessage('title')">
              <h5 class="name">ชื่อ</h5>
              <input
                type="text"
                placeholder="ชื่อวิดีโอ"
                class="form-control"
                :class="{'is-invalid': getErrorMessage('title')}"
                :value="currentVideo.title"
                :disabled="currentVideo.isUsingEpisodeMetadata"
                @blur="$store.dispatch('setCurrentVideo', { title: $event.target.value })"
              />
              <div
                class="invalid-feedback"
                v-if="getErrorMessage('title')">ระบุชื่อของวิดีโอ</div>
            </FormGroup>
            <FormGroup>
              <h5 class="name">รายการ</h5>
              <SelectOptionObject
                :value="currentVideo.program"
                :options="programs"
                :label="'title'"
                :placeholder="'เลือกรายการ'"
                @onChanged="onSelectedProgram"
                @onSearch="loadPrograms" />
            </FormGroup>
            <FormGroup>
              <h5 class="name">ตอน</h5>
              <SelectOptionObject
                :value="currentVideo.episode"
                :options="episodes"
                :label="'label'"
                :placeholder="'เลือกตอน'"
                @onChanged="onSelectedEpisode"
                @onSearch="loadEpisodes" />
            </FormGroup>
            <FormGroup>
              <h5 class="name">หมวดหมู่คอนเทนท์</h5>
               <SelectOptionObject
                :class="{'is-invalid': getErrorMessage('contentCategory')}"
                :value="currentVideo.contentCategory"
                :options="contentCategory"
                :disabled="hasContentCategory()"
                :disabledDeleteButton="true"
                :allowEmpty="false"
                :multiple="true"
                @onChanged="onSelectedContentCategory" />
                <div
                  class="invalid-feedback"
                  v-if="getErrorMessage('contentCategory')">
                  {{getErrorMessage('contentCategory')}}
                </div>
            </FormGroup>
            <FormGroup v-if="hasSeasons()">
              <h5 class="name">ซีซั่น</h5>
              <SelectOptionObject
                :value="currentVideo.programSeason"
                :options="seasons"
                :disabled="currentVideo.isUsingEpisodeMetadata"
                :label="'title'"
                :placeholder="'เลือกซีซั่น'"
                :allowEmpty="false"
                :multiple="true"
                @onChanged="onSelectedSeasons"/>
            </FormGroup>
            <FormGroup>
              <h5 class="name">ช่วงอายุ</h5>
              <SelectOptionObject
                :value="currentVideo.ageCategory"
                :options="ageCategory"
                :disabled="currentVideo.isUsingEpisodeMetadata"
                :placeholder="'เลือกช่วงอายุ'"
                :allowEmpty="false"
                :multiple="true"
                @onChanged="onSelectedAgeCategory" />
            </FormGroup>
            <FormGroup>
              <h5 class="name">รายวิชา</h5>
              <SelectOptionObject
                :value="currentVideo.subjectCategory"
                :options="subjectCategory"
                :disabled="currentVideo.isUsingEpisodeMetadata"
                :placeholder="'เลือกรายวิชา'"
                :allowEmpty="false"
                :multiple="true"
                @onChanged="onSelectedSubjectCategory" />
            </FormGroup>
            <FormGroup v-if="videoCategories">
              <h5 class="name">หมวดหมู่วิดีโอ</h5>
              <SelectOptionObject
                :options="videoCategories"
                :placeholder="'เลือกหมวดหมู่'"
                :value="currentVideo.category"
                @onChanged="onSelectedVideoCategories" />
            </FormGroup>
            <FormGroup>
              <h5 class="name">ออกอากาศครั้งแรก</h5>
              <DateTimePicker
                :value="currentVideo.firstAiredAt"
                @input="(newDate) => $store.dispatch('setCurrentVideo', { firstAiredAt: newDate })"
                :disabled="currentVideo.isUsingEpisodeMetadata"
                placeholder="กรุณาเลือกวันที่" />
            </FormGroup>
            <FormGroup v-if="currentVideo.episode">
              <h5 class="name">รายละเอียดวิดีโอ</h5>
              <div class="form-check">
                <input type="checkbox"
                  class="form-check-input"
                  :checked="currentVideo.isUsingEpisodeMetadata"
                  @change="$store.dispatch('setCurrentVideo', { isUsingEpisodeMetadata: Boolean($event.target.checked) })">
                <label class="form-check-label">
                  ใช้รายละเอียดเหมือนกับตอน <strong>{{currentVideo.episode.title}}</strong>
                </label>
              </div>
            </FormGroup>
          </div>
          <div class="col-md-4">
            <FormGroup>
              <div class="video-container-header">
                <h5 class="name m-0">วิดีโอ</h5>
                <button class="btn btn-link p-0" type="button" @click="onClickChangeVideo">เปลี่ยน</button>
              </div>
              <ImageView
                v-if="!getEmbeddedUrl()"
                imageSize="cover640w360h" />
              <div
                v-if="getEmbeddedUrl() && isBytearkVideo()"
                class="player-container">
                <ByteArkPlayerContainer
                  v-if="getEmbeddedUrl()"
                  :options.sync="videoOptions"/>
              </div>
              <div
                class="video-player-container video--iframe"
                v-if="getEmbeddedUrl() && !isBytearkVideo()">
                <iframe
                  class="video-player"
                  :src="`${getEmbeddedUrl()}?image=${getVideoCoverImage()}`"/>
              </div>
            </FormGroup>
            <FormGroup>
              <h5 class="name">สถานะการเผยแพร่</h5>
              <ContentStatusManagement
                :status="currentVideo.status"
                :startPublish="currentVideo.startPublish"
                :endPublish="currentVideo.endPublish"
                :onChange="onChangeStatus" />
            </FormGroup>
            <FormGroup v-if="currentVideo && currentVideo.createdBy">
              <h5 class="name">สร้างโดย</h5>
              <EditorInformation :editor="currentVideo.createdBy" :timeStamp="currentVideo.createdAt" />
            </FormGroup>
            <FormGroup v-if="currentVideo && currentVideo.updatedBy">
              <h5 class="name">แก้ไขล่าสุดโดย</h5>
              <EditorInformation :editor="currentVideo.updatedBy" :timeStamp="currentVideo.updatedAt" />
            </FormGroup>
          </div>
        </div>
      </form>
    </template>
  </Section>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import { loadTaxonomyByKey } from '@/taxonomy/api';
import { loadPrograms } from '@/program/api';
import { loadEpisodes } from '@/episode/api';
import ByteArkPlayerContainer from '@byteark/byteark-player-vue';
import FormGroup from '@/components/FormGroup.vue';
import Section from '@/components/Section.vue';
import EditorInformation from '@/components/EditorInformation.vue';
import SelectOptionObject from '@/components/SelectOptionObject.vue';
import ContentStatusManagement from '@/contentStatus/ContentStatusManagement.vue';
import DateTimePicker from '@/components/DateTimePicker.vue';
import ImageView from '@/image/ImageView.vue';
import dateFormetter from '@/utils/dateFormatter';

import { previewVideo } from './api';
import VideoUploaderModal from './VideoUploaderModal.vue';

export default {
  mixins: [dateFormetter],
  components: {
    SelectOptionObject,
    FormGroup,
    Section,
    ContentStatusManagement,
    VideoUploaderModal,
    ImageView,
    ByteArkPlayerContainer,
    DateTimePicker,
    EditorInformation,
  },
  data() {
    return {
      videoCategories: null,
      programs: [],
      episodes: [],
      seasons: [],
      uploadModal: false,
      contentCategory: [],
      ageCategory: [],
      subjectCategory: [],
      videoOptions: {},
      defaultVideoOptions: {
        fill: true,
        fluid: false,
        poster: '',
        sources: {},
      },
    };
  },
  created() {
    this.loadVideoCategories();
    this.loadPrograms();
    this.loadEpisodes({ programId: this.currentVideo.programId });
    this.loadContentCategories();
    this.loadAge();
    this.loadSubject();
    if (this.currentVideo.program) {
      this.seasons = this.currentVideo.program.programSeasons;
    }
    if (!this.currentVideo.episode) {
      this.$store.dispatch('setCurrentVideo', { isUsingEpisodeMetadata: false });
    }
    if (this.getEmbeddedUrl()) {
      this.videoOptions = {
        ...this.defaultVideoOptions,
        poster: this.getVideoCoverImage(),
        sources: this.getMediaSources(),
      };
    }
  },
  computed: {
    ...mapState({
      currentVideo: (state) => state.videoItem.currentVideo,
      saveVideoState: (state) => state.videoItem.saveVideoState,
    }),
    isUsingEpisodeMetadata() {
      return _.get(this.currentVideo, 'isUsingEpisodeMetadata', false);
    },
  },
  watch: {
    isUsingEpisodeMetadata(isChecked) {
      if (isChecked) {
        this.$store.dispatch('setCurrentVideo', this.mapEpisodeInformation());
      }
    },
  },
  methods: {
    async loadVideoCategories() {
      const response = await loadTaxonomyByKey('video-category');
      this.videoCategories = response.data.data;
    },
    async loadContentCategories() {
      const response = await loadTaxonomyByKey('content-category');
      this.contentCategory = response.data.data;
    },
    async loadPrograms(filter) {
      const response = await loadPrograms(filter);
      this.programs = response.data.data;
    },
    async loadEpisodes(filter) {
      const programId = _.get(filter, 'programId', this.currentVideo.programId);
      if (!programId) {
        this.episodes = [];
        return;
      }
      const query = { ...filter, programId };
      const response = await loadEpisodes(query);
      this.episodes = response.data.data;
      if (this.episodes && this.episodes.length) {
        this.episodes = this.episodes.map((episode) => {
          return {
            ...episode,
            label: episode.firstAiredAt ? `(${this.formatShortDate(episode.firstAiredAt)}) ${episode.title}` : episode.title,
          };
        });
      }
    },
    async loadAge() {
      const response = await loadTaxonomyByKey('age-category');
      this.ageCategory = response.data.data;
    },
    async loadSubject() {
      const response = await loadTaxonomyByKey('subject-category');
      this.subjectCategory = response.data.data;
    },
    mapEpisodeInformation() {
      const newVideo = {
        ...this.currentVideo,
        title: _.get(this.currentVideo, 'episode.title', ''),
        tags: _.get(this.currentVideo, 'episode.tags', []),
        tagline: _.get(this.currentVideo, 'episode.tagline', ''),
        firstAiredAt: _.get(this.currentVideo, 'episode.firstAiredAt', null),
        description: _.get(this.currentVideo, 'episode.description', ''),
        programSeasonId: _.get(this.currentVideo, 'episode.programSeasonId', null),
        programSeason: _.get(this.currentVideo, 'episode.programSeason', null),
        ageCategory: _.get(this.currentVideo, 'episode.ageCategory', null),
        subjectCategory: _.get(this.currentVideo, 'episode.subjectCategory', null),
        coverImage: _.get(this.currentVideo, 'episode.coverImage', null),
        coverImageCustom: _.get(this.currentVideo, 'episode.coverImage', null),
      };
      return newVideo;
    },
    onSelectedProgram(selectedOption) {
      if (selectedOption) {
        this.seasons = selectedOption.programSeasons;
        this.$store.dispatch('setCurrentVideo', {
          ...this.currentVideo,
          programId: selectedOption.id,
          program: selectedOption,
          programSeason: null,
          programSeasonId: null,
          episode: null,
          episodeId: null,
          isUsingEpisodeMetadata: false,
          contentCategory: _.get(selectedOption, 'contentCategory', this.currentVideo.contentCategory),
          contentCategoryId: _.get(selectedOption, 'contentCategoryId', this.currentVideo.contentCategoryId),
        });
        this.loadEpisodes({ programId: selectedOption.id });
      } else {
        // reset episode when program have been removed
        this.$store.dispatch('setCurrentVideo', {
          ...this.currentVideo,
          program: null,
          programId: null,
          programSeason: null,
          programSeasonId: null,
          episode: null,
          episodeId: null,
          contentCategory: null,
          isUsingEpisodeMetadata: false,
          contentCategoryId: null,
        });
        this.episodes = [];
      }
    },
    onSelectedSeasons(season) {
      if (season) {
        this.$store.dispatch('setCurrentVideo', { programSeason: season, programSeasonId: season.id });
      } else {
        this.$store.dispatch('setCurrentVideo', { programSeason: null, programSeasonId: null });
      }
    },
    onSelectedEpisode(selectedOption) {
      if (selectedOption) {
        this.$store.dispatch('setCurrentVideo', {
          ...this.currentVideo,
          episodeId: selectedOption.id,
          episode: selectedOption,
          isUsingEpisodeMetadata: false,
        });
      } else {
        this.$store.dispatch('setCurrentVideo', {
          ...this.currentVideo,
          episodeId: null,
          episode: null,
          isUsingEpisodeMetadata: false,
        });
      }
    },
    onSelectedVideoCategories(selectedOption) {
      if (selectedOption) {
        this.$store.dispatch('setCurrentVideo', { ...this.currentVideo, category: selectedOption, categoryId: selectedOption.id });
      } else {
        this.$store.dispatch('setCurrentVideo', { ...this.currentVideo, category: null, categoryId: null });
      }
    },
    onSelectedContentCategory(selectedOption) {
      if (selectedOption) {
        this.$store.dispatch('setCurrentVideo', { ...this.currentVideo, contentCategory: selectedOption, contentCategoryId: selectedOption.id });
      }
    },
    onSelectedAgeCategory(age) {
      if (age) {
        this.$store.dispatch('setCurrentVideo', { ageCategory: age });
      }
    },
    onSelectedSubjectCategory(subject) {
      if (subject) {
        this.$store.dispatch('setCurrentVideo', { subjectCategory: subject });
      }
    },
    onChangeStatus(newStatus) {
      this.$store.dispatch('setCurrentVideo', {
        ...this.currentVideo,
        status: newStatus.status,
        startPublish: newStatus.startPublish,
        endPublish: newStatus.endPublish,
      });
    },
    async onVideoChange(media) {
      const filter = {
        type: media.type,
        sourceKey: media.sourceKey,
        sourceUrl: media.sourceUrl,
      };

      const response = await previewVideo(filter);
      const { embeddedUrl, canonicalUrl } = response.data;
      let newMedia = {
        sourceUrl: media.sourceUrl,
        type: media.type,
        sourceKey: media.sourceKey,
        embeddedUrl,
      };

      if (this.isBytearkVideo()) {
        newMedia = {
          ...newMedia,
          sources: [
            {
              src: media.sourceUrl,
              type: 'application/x-mpegURL',
            },
          ],
        };
      } else {
        newMedia = {
          ...newMedia,
          canonicalUrl,
        };
      }
      await this.$store.dispatch('setCurrentVideo', { media: newMedia });
      this.onCloseModal();
    },
    onDeleteVideo() {
      this.$store.dispatch('setCurrentVideo', { ...this.currentVideo, media: null });
      this.onCloseModal();
    },
    onClickChangeVideo() {
      this.uploadModal = true;
    },
    onCloseModal() {
      this.uploadModal = false;
    },
    isBytearkVideo() {
      return this.getVideoType() === 'qoder' || this.getVideoType() === 'byteark';
    },
    getVideoCoverImage() {
      return _.get(this.currentVideo, 'coverImage.urls.cover1280w720h', '');
    },
    hasContentCategory() {
      const category = _.get(this.currentVideo, 'program.contentCategoryId', null);
      return !!category;
    },
    getEmbeddedUrl() {
      return _.get(this.currentVideo, 'media.embeddedUrl', '');
    },
    getMediaSources() {
      return _.get(this.currentVideo, 'media.sources', null);
    },
    getVideoType() {
      return _.get(this.currentVideo, 'media.type', '');
    },
    getErrorMessage(fieldName) {
      return _.get(this.saveVideoState.error, `${fieldName}`, null);
    },
    hasSeasons() {
      if (this.currentVideo.program) {
        if (this.currentVideo.program.hasSeason) return true;
        return false;
      }
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/form.scss';
@import './src/assets/scss/themes/components/section-container.scss';

.video-container-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacer / 3;
}

// iFrame Player Container
.video-player-container {
  &.video--iframe {
    padding-top: 56.25%;
    width: 100%;
    position: relative;
    .video-player {
      border: 0;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
    }
  }
}

// ByteArk Player Fill Container
.player-container {
  width: 100%;
  height: 190px;
  position: relative;
}

@media screen and (max-width: 768px) {
  // ByteArk Player Fill Container
  .player-container {
    height: 48vw;
  }
}

</style>
